// QuantumGambit - Informatique Quantique
// Histoire : Un labo quantique en crise face a la menace de l algorithme de Shor

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface QuantumGambitScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const quantumGambitCharacters: Character[] = [
  { id: 'yuki',   name: 'Dr. Yuki Tanaka', role: 'Quantum Researcher', color: '#0F4C75', avatar: 'yuki'   },
  { id: 'marcus', name: 'Marcus Wright',   role: 'CISO',               color: '#1B4332', avatar: 'marcus' },
];

// ── Chapitre I : Les Qubits de la Discorde ───────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'qg-scene-1',
  title: 'Scene 1 : L algorithme qui fait peur',
  duration: 4,
  content: {
    narrativeText:
      "Centre de Recherche en Informatique Quantique. 14h32.\n\n" +
      "**Dr. Yuki Tanaka :** \"Les resultats viennent de tomber. Notre implementation de l algorithme de Shor a factorise une cle RSA-2048 en 8 heures sur notre simulateur 127 qubits.\"\n\n" +
      "**Marcus Wright :** \"Huit heures. Aujourd hui c est un simulateur. Dans trois ans, ce sera une machine physique.\"\n\n" +
      "**Dr. Yuki Tanaka :** \"Le CTO veut une migration vers la cryptographie post-quantique en 6 mois. Tout notre stack de securite repose sur RSA et ECC.\"\n\n" +
      "**Marcus Wright :** \"Combien de systemes sont touches ?\"\n\n" +
      "**Dr. Yuki Tanaka :** \"TLS, SSH, nos signatures numeriques, les echanges de cles internes... Des centaines de services. Et le NIST vient de finaliser ses standards post-quantiques : CRYSTALS-Kyber pour le KEM, CRYSTALS-Dilithium pour les signatures.\"\n\n" +
      "**Marcus Wright :** \"On a besoin d un expert en cryptographie. C est pour ca que tu es la.\"\n\n" +
      "**Dr. Yuki Tanaka :** \"Commençons par les fondamentaux. Pour comprendre pourquoi l algorithme de Shor est si dangereux, il faut comprendre ce qu est un qubit.\"",
    characters: ['yuki', 'marcus'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'qg-card-1',
  title: 'Concept : Qubits et Superposition Quantique',
  duration: 5,
  content: {
    title: 'Informatique Quantique — Principes fondamentaux',
    body:
      "## Qubits vs Bits classiques\n\n" +
      "Un **bit** classique est soit 0, soit 1. Un **qubit** peut etre dans une superposition des deux etats simultanement.\n\n" +
      "### Superposition\n" +
      "Un qubit est decrit par : |ψ⟩ = α|0⟩ + β|1⟩\n" +
      "- α et β sont des amplitudes complexes\n" +
      "- |α|² + |β|² = 1 (probabilite totale = 1)\n" +
      "- La mesure force le qubit dans l etat 0 ou 1 (effondrement de la fonction d onde)\n\n" +
      "### Intrication (Entanglement)\n" +
      "Deux qubits intrigues forment un systeme indivisible : mesurer l un instantanement determine l etat de l autre, quelle que soit la distance.\n\n" +
      "### Portes quantiques\n" +
      "| Porte | Effet | Equivalent classique |\n" +
      "|---|---|---|\n" +
      "| Hadamard (H) | Cree une superposition | Aucun |\n" +
      "| CNOT | Porte conditionnelle a 2 qubits | XOR |\n" +
      "| Toffoli | Porte conditionnelle a 3 qubits | AND conditionnel |\n" +
      "| Phase (S, T) | Rotation de phase | Aucun |\n\n" +
      "### Pourquoi c est puissant ?\n" +
      "N qubits peuvent representer 2^N etats simultanement. 300 qubits = plus d etats que d atomes dans l univers observable.\n\n" +
      "### Algorithme de Shor\n" +
      "Utilise la transformee de Fourier quantique pour factoriser des entiers en temps polynomial O((log N)³) au lieu de O(e^(log N)^(1/3)) classique. **RSA est casse.**",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'qg-chat-1',
  title: 'Discussion avec Dr. Tanaka : Qubits vs Bits',
  duration: 6,
  content: {
    tutorCharacterId: 'yuki',
    messages: [
      {
        id: 'qg-chat-1-q1',
        sender: 'character',
        characterId: 'yuki',
        text: "Quelle est la difference fondamentale entre un qubit et un bit classique ?",
        type: 'single-choice',
        options: [
          "Un qubit stocke plus de donnees car il est physiquement plus grand",
          "Un qubit peut etre dans une superposition de 0 et 1 simultanement jusqu a sa mesure",
          "Un qubit est identique a un bit mais 100x plus rapide",
          "Un qubit peut prendre les valeurs 0, 1 et 2",
        ],
        correctAnswer: "Un qubit peut etre dans une superposition de 0 et 1 simultanement jusqu a sa mesure",
        explanation: "La superposition est la propriete fondamentale qui distingue les qubits. Avant mesure, un qubit existe dans une combinaison lineaire des etats |0⟩ et |1⟩. La mesure fait effondrer cet etat vers 0 ou 1 avec des probabilites determinees par les amplitudes.",
      },
      {
        id: 'qg-chat-1-q2',
        sender: 'character',
        characterId: 'yuki',
        text: "Pourquoi l algorithme de Shor menace-t-il specifiquement RSA ?",
        type: 'single-choice',
        options: [
          "Parce qu il peut deviner les mots de passe plus rapidement",
          "Parce qu il factorise des grands nombres en temps polynomial, brisant la securite de RSA",
          "Parce que les ordinateurs quantiques sont plus puissants que les classiques pour tout calcul",
          "Parce qu il exploite une faille dans le protocole TLS",
        ],
        correctAnswer: "Parce qu il factorise des grands nombres en temps polynomial, brisant la securite de RSA",
        explanation: "La securite de RSA repose sur la difficulte de factoriser le produit de deux grands nombres premiers. L algorithme de Shor resout ce probleme en O((log N)³), rendant RSA vulnerable. Un ordinateur quantique suffisamment grand pourrait casser RSA-2048 en quelques heures.",
      },
      {
        id: 'qg-chat-1-q3',
        sender: 'character',
        characterId: 'yuki',
        text: "Qu est-ce que l intrication quantique (entanglement) ?",
        type: 'single-choice',
        options: [
          "Un bug qui ralentit les calculs quantiques",
          "Un etat ou deux qubits sont correles : mesurer l un determine instantanement l etat de l autre",
          "Une technique pour accelerer les portes quantiques",
          "Le processus d initialisation d un qubit",
        ],
        correctAnswer: "Un etat ou deux qubits sont correles : mesurer l un determine instantanement l etat de l autre",
        explanation: "L intrication cree une correlation non-locale entre qubits. Einstein appelait ca 'spooky action at a distance'. Cette propriete est exploitee dans les algorithmes quantiques pour traiter des informations en parallele de maniere fondamentalement differente du calcul classique.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'qg-task-1',
  title: 'Evaluer la vulnerabilite des algorithmes RSA face a Shor',
  duration: 25,
  content: {
    platform: 'Analyse theorique et outils cryptographiques',
    objective: "Identifier et prioriser les algorithmes vulnerables dans le stack cryptographique",
    azurePortalPath: 'Analyse du stack cryptographique existant',
    steps: [
      {
        stepNumber: 1,
        action: "Inventorier les algorithmes utilises",
        detail: "Lister tous les algorithmes cryptographiques en production : RSA-2048 (TLS, SSH), ECDSA P-256 (signatures), ECDH (echange de cles), AES-256 (chiffrement symetrique). Identifier leur vulnerabilite au quantum.",
      },
      {
        stepNumber: 2,
        action: "Evaluer le risque par algorithme",
        detail: "RSA et ECC : vulnerables a Shor (asymetrique). AES-256 : semi-vulnerable a Grover (symetrique, mais doublement de la cle suffit). SHA-256/SHA-3 : resistants avec cles suffisantes. Prioriser RSA et ECC.",
      },
      {
        stepNumber: 3,
        action: "Identifier les standards post-quantiques NIST",
        detail: "CRYSTALS-Kyber (ML-KEM) : remplacement pour les echanges de cles. CRYSTALS-Dilithium (ML-DSA) : remplacement pour les signatures numeriques. SPHINCS+ : signatures basees sur les fonctions de hachage.",
      },
      {
        stepNumber: 4,
        action: "Planifier la migration par priorite",
        detail: "Etape 1 : TLS 1.3 avec hybrid mode (classique + post-quantique). Etape 2 : Remplacement SSH par des cles post-quantiques. Etape 3 : Migration des signatures numeriques internes.",
      },
      {
        stepNumber: 5,
        action: "Documenter le rapport de risque",
        detail: "Creer un rapport avec : nombre de services touches, criticite, delai de migration estime, et recommendation de priorite pour le CTO.",
      },
    ],
    validation: "Rapport de vulnerabilite complete. Stack cryptographique audite. Plan de migration vers CRYSTALS-Kyber et Dilithium propose.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'qg-quiz-1',
  title: 'Quiz : Informatique Quantique — Fondamentaux',
  duration: 5,
  content: {
    questions: [
      {
        id: 'qg-q1-1',
        question: "Combien d etats peut representer simultanement un registre de 10 qubits en superposition ?",
        type: 'single-choice',
        options: [
          "10 etats",
          "20 etats",
          "1024 etats (2^10)",
          "100 etats",
        ],
        correctAnswer: "1024 etats (2^10)",
        explanation: "N qubits en superposition representent 2^N etats simultanement. C est l avantage exponentiel du calcul quantique : 10 qubits = 1024 etats, 50 qubits = plus de 10^15 etats, 300 qubits = plus d etats que d atomes dans l univers.",
      },
      {
        id: 'qg-q1-2',
        question: "Quel algorithme post-quantique le NIST recommande-t-il pour les echanges de cles (KEM) ?",
        type: 'single-choice',
        options: [
          "RSA-4096",
          "CRYSTALS-Kyber (ML-KEM)",
          "Diffie-Hellman classique",
          "AES-512",
        ],
        correctAnswer: "CRYSTALS-Kyber (ML-KEM)",
        explanation: "Le NIST a standardise CRYSTALS-Kyber (renomme ML-KEM) comme algorithme de Key Encapsulation Mechanism post-quantique. Il est base sur le probleme de learning with errors (LWE), considere resistant aux attaques quantiques.",
      },
      {
        id: 'qg-q1-3',
        question: "La porte de Hadamard (H) appliquee sur un qubit |0⟩ produit quel etat ?",
        type: 'single-choice',
        options: [
          "L etat |1⟩ directement",
          "Une superposition egale de |0⟩ et |1⟩",
          "Un qubit enchevetre avec un autre",
          "L etat |0⟩ reste inchange",
        ],
        correctAnswer: "Une superposition egale de |0⟩ et |1⟩",
        explanation: "H|0⟩ = (|0⟩ + |1⟩)/√2 — une superposition ou les probabilites de mesurer 0 ou 1 sont chacune de 50%. La porte Hadamard est fondamentale : elle transforme la base computationnelle en base de Fourier.",
      },
    ],
  } as QuizContent,
};

const scene2: SequenceItem = {
  type: 'story',
  id: 'qg-scene-2',
  title: 'Scene 2 : Test du premier algorithme post-quantique',
  duration: 4,
  content: {
    narrativeText:
      "Laboratoire, 3 semaines plus tard. Le prototype de migration TLS hybride est pret.\n\n" +
      "**Dr. Yuki Tanaka :** \"Le premier service est migre en mode hybrid — TLS avec ECDH classique ET CRYSTALS-Kyber en parallele. Les deux doivent etre compromis pour casser la connexion.\"\n\n" +
      "**Marcus Wright :** \"Overhead de performance ?\"\n\n" +
      "**Dr. Yuki Tanaka :** \"Augmentation de 3% de la latence. Acceptable. La taille des cles Kyber-768 est plus grande que ECDH, mais les algorithmes sont tres rapides.\"\n\n" +
      "**Marcus Wright :** \"Et pour les signatures ?\"\n\n" +
      "**Dr. Yuki Tanaka :** \"CRYSTALS-Dilithium en cours de deploiement. Les certificats X.509 vont etre regeneres avec des signatures hybrides RSA + Dilithium pendant la periode de transition.\"\n\n" +
      "**Marcus Wright :** \"Harvest now, decrypt later — l attaquant qui enregistre notre trafic aujourd hui ne pourra pas le dechiffrer quand les quantiques arriveront.\"\n\n" +
      "**Dr. Yuki Tanaka :** \"Exactement. La migration n est pas juste une mesure future — c est une protection immediate contre la menace HNDL.\"\n\n" +
      "**Marcus Wright :** \"Six mois. On y arrivera.\"",
    characters: ['yuki', 'marcus'],
  } as StoryContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'Les Qubits de la Discorde',
  subtitle: 'Informatique Quantique — Principes fondamentaux et menace post-quantique',
  learningObjectives: [
    "Comprendre les principes fondamentaux des qubits et de la superposition",
    "Analyser la menace de l algorithme de Shor sur la cryptographie RSA",
    "Identifier les algorithmes post-quantiques standardises par le NIST",
    "Planifier une migration cryptographique vers les standards post-quantiques",
    "Comprendre le mode hybrid pour la transition cryptographique",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1, scene2],
};

export const quantumGambitScenario: QuantumGambitScenario = {
  id: 'quantum-gambit',
  title: 'Le Pari Quantique',
  subtitle: "L algorithme de Shor change toutes les regles",
  certification: 'Quantum Computing',
  coverImage: require('../../../assets/images/characters/data_storm_jun_woman_30s.jpg'),
  characters: quantumGambitCharacters,
  chapters: [chapter1],
};

export default quantumGambitScenario;
